import moment from 'moment';
import { defaultDateFormat } from '../../../constants';

interface WorkspaceLike {
  startDate?: string | null;
  source?: string | null;
}

/**
 * Resolve the workspace's "current day" without reading the URL: the last viewed
 * date persisted by the App date reaction (localStorage), falling back to the
 * workspace anchor date / today. Shared by the routes page's `?date=` restore
 * (dateValidator) and the scheduling subflow's default date
 * (openSchedulingPreview), so both resolve the same day regardless of how the
 * user navigated here — including through dateless pages (customers, drivers).
 */
const resolveLastViewedDate = (workspace?: WorkspaceLike | null): string => {
  const stored = localStorage.getItem('selectedWorkspaceDate');
  const rawStartDate = workspace?.startDate;
  const today = moment().format(defaultDateFormat);

  // startDate can arrive in a non-YYYY-MM-DD shape (e.g. an ISO timestamp), so
  // normalize it once — then the comparison below is a plain date comparison
  // rather than relying on lexicographic ordering of an arbitrary string.
  const startDate = rawStartDate && (moment(rawStartDate, defaultDateFormat, true).isValid()
    ? rawStartDate
    : moment.utc(rawStartDate).format(defaultDateFormat));

  // API-scaffolded workspaces live entirely on their internal anchor date, which
  // is often in the past — clamping to today would land on an empty plan.
  const isApiWorkspace = workspace?.source === 'api';

  // develop used `stored || startDate || today`. ROUT-2673: clamp the startDate
  // fallback (latest date with routes) so we never land before today —
  // i.e. max(today, startDate). The last viewed day (stored) is still used as-is.
  const chosen = stored || (startDate && (isApiWorkspace || startDate > today) ? startDate : today);

  return moment(chosen, defaultDateFormat, true).isValid() ? chosen : moment.utc(chosen).format(defaultDateFormat);
};

export default resolveLastViewedDate;
